import React, { lazy, useEffect } from 'react'
import { Switch, Redirect, Route, useLocation } from 'react-router-dom'

import * as R from 'ramda'
import { connect } from 'react-redux'
import { lifecycle, compose, withState, withStateHandlers, withHandlers } from 'recompose'
import { VisibilityProvider } from 'hooks/useVisibility'
import { QueryClientProvider, QueryClient } from 'react-query'
import { withNamespaces } from 'react-i18next'

import MuiFab from '@material-ui/core/Fab'
import MuiPopover from '@material-ui/core/Popover'
import MuiMenuItem from '@material-ui/core/MenuItem'
import { withStyles } from '@material-ui/core/styles'
import MuiLinearProgress from '@material-ui/core/LinearProgress'
import MuiCircularProgress from '@material-ui/core/CircularProgress'

import MuiIconHeadsetMicOutlined from '@material-ui/icons/HeadsetMicOutlined'

import CsgCentralizedTemplate from 'components/templates/Centralized'
import CsgLazyRoute from 'components/molecules/LazyRoute'
import LocaleToggler from 'components/organisms/LocaleToggler'

import authService from 'services/auth'

// Google Analytics
import ReactGA from 'react-ga'

// GTM
import TagManager from 'react-gtm-module'

import { TwoFactorProvider } from 'hooks/useTwoFactor'
import { CountdownProvider } from 'hooks/useCountdown'
import { MatriculaProvider } from 'hooks/useMatricula'
import { SimulatedMarginProvider } from 'hooks/useSimulatedMargin'
import { MatriculaModalPaginationProvider } from 'hooks/useMatriculaModalPagination'
import config from 'app/config'
import { loadFreshChatWidget } from 'app/freshchat'
import WidgetFeedbackRating from 'components/organisms/WidgetFeedbackRating'

const CsgAuthPage = lazy(() => import('components/pages/Auth'))
const CsgAdminPage = lazy(() => import('components/pages/Admin'))
const CsgNotFoundPage = lazy(() => import('components/pages/NotFound'))

const styles = theme => ({
  root: {
    position: 'relative'
  },
  loading: {
    top: 0,
    left: 0,
    width: '100%',
    height: 5,
    position: 'fixed',
    zIndex: theme.zIndex.loading
  },
  linearColorPrimary: {
    backgroundColor: theme.palette.warning.main
  },
  linearBarColorPrimary: {
    backgroundColor: theme.palette.warning.light
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:active': {
      color: 'inherit'
    },
    marginTop: 7
  },
  fab: {
    position: 'fixed',
    bottom: 33,
    right: 110,
    borderRadius: '30px 8px 30px 30px',
    width: 74,
    height: 32
  }
})

const LoadingComponents = () => (
  <CsgCentralizedTemplate>
    <MuiCircularProgress />
  </CsgCentralizedTemplate>
)

const queryClient = new QueryClient()

const App = ({
  t,
  me,
  classes,
  loading,
  setAnchorFab,
  openPopOver,
  popoverOpened,
  anchorFab,
  closePopOver,
  openFreshChat
}) => {
  const { pathname } = useLocation()

  const shouldShowChatWidget = () => {
    return pathname.includes('admin') && !config.project.isVan && !config.isDev
  }

  const shouldShowFeedbackRatingWidget = () => {
    return pathname.includes('admin')
  }

  useEffect(() => {
    if (!window.fcWidget && shouldShowChatWidget()) {
      loadFreshChatWidget()
    }
  }, [pathname])

  return R.isNil(me) ? (
    <LoadingComponents />
  ) : (
    <QueryClientProvider client={queryClient}>
      <TwoFactorProvider>
        <CountdownProvider>
          <VisibilityProvider>
            <SimulatedMarginProvider>
              <MatriculaProvider>
                <MatriculaModalPaginationProvider>
                  <div className={classes.root}>
                    {loading && (
                      <MuiLinearProgress
                        className={classes.loading}
                        classes={{
                          colorPrimary: classes.linearColorPrimary,
                          barColorPrimary: classes.linearBarColorPrimary
                        }}
                      />
                    )}

                    <LocaleToggler />

                    {shouldShowFeedbackRatingWidget() && (
                      <WidgetFeedbackRating shouldTranslateX={shouldShowChatWidget()} />
                    )}

                    <Switch>
                      <CsgLazyRoute path="/admin" component={CsgAdminPage} />
                      <CsgLazyRoute path="/auth" component={CsgAuthPage} />
                      <Route path="/" exact render={() => <Redirect to="/auth/login" />} />
                      <CsgLazyRoute component={CsgNotFoundPage} />
                    </Switch>

                    {shouldShowChatWidget() && (
                      <>
                        <MuiFab color="primary" onClick={openPopOver} className={classes.fab}>
                          <span className={classes.link} ref={setAnchorFab} rel="noopener noreferrer">
                            <MuiIconHeadsetMicOutlined />
                          </span>
                        </MuiFab>

                        <MuiPopover
                          open={popoverOpened}
                          anchorEl={anchorFab}
                          onClose={closePopOver}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                          }}
                          transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                          }}
                        >
                          <div>{<MuiMenuItem onClick={openFreshChat}>{t('online_chat')}</MuiMenuItem>}</div>
                        </MuiPopover>
                      </>
                    )}
                  </div>
                </MatriculaModalPaginationProvider>
              </MatriculaProvider>
            </SimulatedMarginProvider>
          </VisibilityProvider>
        </CountdownProvider>
      </TwoFactorProvider>
    </QueryClientProvider>
  )
}
const mapStateToProps = ({ loading, me }) => ({
  me,
  ...loading
})
const enhance = compose(
  withStyles(styles),
  withNamespaces('translation'),
  connect(mapStateToProps),
  withState('anchorFab', 'setAnchorFab', null),
  withState('hasWarning', 'setHasWarning', false),
  withStateHandlers(
    () => ({
      popoverOpened: false
    }),
    {
      openPopOver: () => () => ({ popoverOpened: true }),
      closePopOver: () => () => ({ popoverOpened: false })
    }
  ),
  withHandlers({
    openFreshChat: ({ closePopOver }) => () => {
      closePopOver()
      if (window.fcWidget) window.fcWidget.open()
    },
    widget: () => () => {
      let idIFrame = document.getElementById('launcher-frame')
      if (idIFrame !== null) {
        document.getElementById('launcher-frame').style.maxWidth = '0px'
        document.getElementById('launcher-frame').style.minWidth = '84px'
      }
    }
  }),
  lifecycle({
    componentWillUpdate() {
      this.props.widget()
    },
    async componentDidMount() {
      initializeReactGA()
      initializeReactGTM()
      if (!config.project.isVan) {
        await authService.onLoadWidgetSuporte()
        window.FreshworksWidget('hide', 'launcher')
      }
      await authService.me()
    }
  })
)

function initializeReactGA() {
  ReactGA.initialize('UA-156197772-1')
  ReactGA.pageview(window.location.pathname)
}

const tagManagerArgs = {
  gtmId: 'GTM-PBKZVLH5'
}

function initializeReactGTM() {
  TagManager.initialize(tagManagerArgs)
}

export default enhance(App)
